// # vendor
import * as React from 'react';
import { boundMethod } from 'autobind-decorator';
import { Editor as TinyMCE } from '@tinymce/tinymce-react';
// # constants
import { defaultProps } from './defaultProps';
// # interfaces
import { iContentEditorProps } from './interfaces';
// # models
import { FileModel } from '@/models';
// # services
import { uploadsService } from '@/services/api';

interface iState {
    initialised: boolean;
}
/**
 * Wraps the TinyMCE editor and provides configuration.
 */
export class ContentEditor extends React.Component<iContentEditorProps, iState> {
    static defaultProps = defaultProps;

    state = {
        initialised: false,
    };

    /**
     * The TinyMCE editor instance
     *
     * @type {object}
     */
    public editorInstance: any = null;

    /**
     * Renders the content.
     *
     * @returns {React.ReactNode}
     */
    public render(): React.ReactNode {
        const { config, plugins, toolbarsGrouped, value } = this.props;
        const { initialised } = this.state;
        return (
            <>
                {!initialised ? <p>Loading editor...</p> : null }

                <TinyMCE
                    apiKey="fxh59thf691nou8kowxk080i1gjkgkumsmsgj116hnb9r9l3"
                    init={{
                        ...config,
                        // @ts-ignore
                        images_upload_handler: this.imageUploadHandler,
                    }}
                    onEditorChange={this.handleContentChange}
                    onInit={this.onInitialised}
                    plugins={plugins.join(' ')}
                    toolbar={this.makeToolbarString(toolbarsGrouped)}
                    value={value}
                />
            </>
        );
    }

    /**
     * Calls the onChange prop when the TinyMCE content changes.
     *
     * @param {string} content
     * @returns {void}
     */
    @boundMethod
    protected handleContentChange(content: string): void {
        // sometimes this gets called with a change object instead of a string, whyyyy
        if (typeof content !== 'string') { return; }

        this.props.onChange(content);
    }

    /**
     * Uploads a selected image to the API
     *
     * @param {object} fileInfo
     * @param {() => void} successCallback
     * @param {() => void} failureCallback
     * @returns {void}
     */
    protected imageUploadHandler(
        fileInfo: {
            base64: () => string,
            blob: () => Blob,
        },
        successCallback: () => void,
        failureCallback: () => void,
    ) {
        const file = FileModel.fromBlob(fileInfo.blob());
        uploadsService.save(file)
            .then(response => response.src)
            .then(successCallback)
            .catch(failureCallback);
    }

    /**
     * Builds the toolbar string prop for TinyMCE.
     *
     * @param {string[][][]} groups
     * @returns {string}
     */
    protected makeToolbarString(groups: string[][][]): string[] {
        const reducer = (toolbar: string, items: string[]) => `${toolbar} | ${items.join(' ')}`;
        return groups.map(group => group.reduce<string>(reducer, '').trim());
    }

    /**
     * Handles the TinyMCE onInit callback.
     * Caches the editor instance.
     *
     * @param {any} evt
     * @param {any} editor
     * @returns {void}
     */
    @boundMethod
    protected onInitialised(evt: any, editor: any): void {
        this.editorInstance = editor;
        this.setState({ initialised: true });
    }
}

export default ContentEditor;
